// client/src/pages/secure/WebsiteChatPage.js — Website Chat for the managing broker and office manager.
//
//   Inbox        live conversations waiting for a person, and visitor requests (showings,
//                callbacks, questions); take a conversation over and reply in it.
//   Outbox       messages waiting for a person (auto-send off, approval required, or no
//                provider yet). Approve & send runs Contact Compliance first.
//   Sends        every email and text the portal sent or tried to (lib/outbound), with
//                the compliance decision and history; texts received (STOP/HELP/replies).
//   Transcripts  every conversation, kept for the records retention period (broker only).
//   Settings     tiers, model, spending caps, hours, greeting, consent wording, FAQ.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';
import { OutboxList, SendsDashboard, InboundTexts, OutboundSettings } from '../../components/secure/OutboundPanels';

const API = '/api/chat-admin';
const card = 'bg-white rounded-lg shadow p-4';
const btn = 'px-3 py-1.5 rounded text-sm';
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');
const MODE_ORDER = ['waiting_human', 'human', 'bot'];
const modeLabel = (m) => (m === 'waiting_human' ? 'waiting for a person' : m);

function Transcript({ api, id, live, me, onChanged }) {
    const [data, setData] = useState(null);
    const [reply, setReply] = useState('');
    const [err, setErr] = useState('');
    const load = useCallback(() => api.get(`${API}/conversations/${id}`).then(setData).catch((e) => setErr(e.message)), [api, id]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!live) return undefined;
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, [live, load]);
    const act = async (path, body) => { setErr(''); try { await api.post(`${API}/conversations/${id}/${path}`, body || {}); await load(); onChanged && onChanged(); } catch (e) { setErr(e.message); } };
    const send = async (e) => { e.preventDefault(); if (!reply.trim()) return; await act('messages', { body: reply }); setReply(''); };
    if (!data) return <p className="text-sm text-slate-500">{err || 'Loading…'}</p>;
    const c = data.conversation;
    const mine = c.conv_mode === 'human' && Number(c.assigned_user_id) === Number(me?.userId);
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center text-sm">
                <span className="font-semibold">Conversation C-{c.conversation_id}</span>
                <span className="px-2 py-0.5 rounded bg-slate-100">{c.conv_mode}</span>
                {c.flagged ? <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">flagged: {c.flag_reasons}</span> : null}
                {c.consent_at && <span className="text-xs text-slate-500">consent given {fmt(c.consent_at)} (v{c.consent_version})</span>}
                <span className="flex-1" />
                {(c.conv_mode === 'waiting_human' || c.conv_mode === 'bot') && <button className={`${btn} bg-slate-800 text-white`} onClick={() => act('take')}>Take over</button>}
                {mine && <button className={`${btn} border`} onClick={() => act('release')}>Hand back to the assistant</button>}
                {c.conv_mode !== 'closed' && <button className={`${btn} border`} onClick={() => act('close')}>End conversation</button>}
            </div>
            {err && <p className="text-sm text-red-600">{err}</p>}
            <ul className="space-y-1 max-h-96 overflow-y-auto border rounded p-2 bg-slate-50">
                {data.messages.map((m) => (
                    <li key={m.message_id} className="text-sm">
                        <span className={`font-semibold ${m.sender === 'guest' ? 'text-indigo-700' : m.sender === 'staff' ? 'text-emerald-700' : 'text-slate-600'}`}>
                            {m.sender === 'guest' ? 'Visitor' : m.sender === 'staff' ? 'Staff' : m.sender === 'bot' ? `Assistant (${m.tier})` : 'System'}
                        </span>{' '}<span className="text-xs text-slate-400">{fmt(m.created_at)}</span>
                        <div className="whitespace-pre-line">{m.body}</div>
                        {m.guardrail && <div className="text-xs text-amber-700">guardrail: {m.guardrail}</div>}
                        {Array.isArray(m.tool_calls) && m.tool_calls.length > 0 && (
                            <div className="text-xs text-slate-500">tools: {m.tool_calls.map((t) => `${t.name}${t.refused ? ` (refused: ${t.refused})` : t.ok ? '' : ' (failed)'}`).join(', ')}</div>
                        )}
                    </li>
                ))}
            </ul>
            {mine && (
                <form onSubmit={send} className="flex gap-2">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} maxLength={2000} placeholder="Reply to the visitor…" className="flex-1 p-2 border rounded text-sm" aria-label="Reply" />
                    <button className={`${btn} bg-slate-800 text-white`}>Send</button>
                </form>
            )}
            {data.handoffs.length > 0 && (
                <div className="text-xs text-slate-600">Requests: {data.handoffs.map((h) => `H-${h.handoff_id} ${h.request_type} (${h.status})`).join('; ')}</div>
            )}
        </div>
    );
}

function Inbox({ api, me }) {
    const [data, setData] = useState({ conversations: [], handoffs: [] });
    const [sel, setSel] = useState(null);
    const [err, setErr] = useState('');
    const load = useCallback(() => api.get(`${API}/inbox`).then(setData).catch((e) => setErr(e.message)), [api]);
    useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
    const resolve = async (id, cancel) => { try { await api.post(`${API}/handoffs/${id}/resolve`, { cancel }); load(); } catch (e) { setErr(e.message); } };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={`${card} space-y-4`}>
                {err && <p className="text-sm text-red-600">{err}</p>}
                <div>
                    <h3 className="font-semibold text-sm mb-1">Conversations</h3>
                    <DataList rows={data.conversations} rowKey="conversation_id" csvName="chat-conversations" onRowClick={(c) => setSel(c.conversation_id)}
                        defaultSort={{ key: 'conv_mode', dir: 'asc' }} empty="No one is chatting right now."
                        rowClass={(c) => (sel === c.conversation_id ? 'bg-indigo-50' : '')}
                        columns={[
                            { key: 'conversation_id', label: 'Chat', value: (c) => `C-${c.conversation_id}`, sortValue: (c) => c.conversation_id },
                            { key: 'client_name', label: 'Visitor' },
                            { key: 'conv_mode', label: 'Mode', value: (c) => modeLabel(c.conv_mode), sortValue: (c) => MODE_ORDER.indexOf(c.conv_mode),
                                render: (c) => <span className="whitespace-nowrap"><span className={`inline-block w-2 h-2 rounded-full mr-2 ${c.conv_mode === 'waiting_human' ? 'bg-red-500' : c.conv_mode === 'human' ? 'bg-emerald-500' : 'bg-slate-300'}`} aria-hidden="true" /><span className="text-xs text-slate-500">{modeLabel(c.conv_mode)}</span></span> },
                            { key: 'last_message_at', label: 'Last message', type: 'date', render: (c) => fmt(c.last_message_at) },
                        ]} />
                </div>
                <div>
                    <h3 className="font-semibold text-sm mb-1">Visitor requests</h3>
                    <DataList rows={data.handoffs} rowKey="handoff_id" csvName="chat-visitor-requests" empty="None open."
                        defaultSort={{ key: 'created_at', dir: 'desc' }} filters={[{ key: 'request_type', label: 'Request' }]}
                        columns={[
                            { key: 'handoff_id', label: 'Request', value: (h) => `H-${h.handoff_id}`, sortValue: (h) => h.handoff_id },
                            { key: 'request_type', label: 'Type' },
                            { key: 'client_name', label: 'Visitor' },
                            { key: 'address', label: 'Address', value: (h) => (h.street_address ? `${h.street_address}, ${h.city || ''}` : '') },
                            { key: 'reason', label: 'Reason' },
                            { key: 'preferred_time', label: 'Prefers' },
                            { key: 'agent_name', label: 'Agent', value: (h) => h.agent_name || 'not yet assigned' },
                            { key: 'created_at', label: 'Asked', type: 'date', render: (h) => fmt(h.created_at) },
                            { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (h) => (
                                <div className="flex gap-2 whitespace-nowrap">
                                    <button className="text-xs underline" onClick={() => setSel(h.conversation_id)}>Open chat</button>
                                    {h.request_type === 'valuation' && <Link className="text-xs underline text-indigo-700" to={`/dashboard/cma?new=1&handoff_id=${h.handoff_id}`}>Prepare CMA</Link>}
                                    <button className="text-xs underline" onClick={() => resolve(h.handoff_id, false)}>Done</button>
                                    <button className="text-xs underline text-slate-500" onClick={() => resolve(h.handoff_id, true)}>Cancel</button>
                                </div>) },
                        ]} />
                </div>
            </div>
            <div className={`${card} lg:col-span-2`}>
                {sel ? <Transcript key={sel} api={api} id={sel} live me={me} onChanged={load} /> : <p className="text-sm text-slate-500">Pick a conversation.</p>}
            </div>
        </div>
    );
}

function Transcripts({ api, me }) {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState({ flagged: false, q: '', from: '', to: '' });
    const [sel, setSel] = useState(null);
    const [ret, setRet] = useState(null);
    const [err, setErr] = useState('');
    const load = useCallback(() => {
        const p = new URLSearchParams();
        if (q.flagged) p.set('flagged', '1');
        ['q', 'from', 'to'].forEach((k) => { if (q[k]) p.set(k, q[k]); });
        api.get(`${API}/transcripts?${p}`).then(setRows).catch((e) => setErr(e.message));
        api.get(`${API}/retention`).then(setRet).catch(() => {});
    }, [api, q]);
    useEffect(() => { load(); }, [load]);
    const purge = async () => {
        if (!window.confirm(`Delete ${ret.count} transcript(s) older than ${ret.years} years? This cannot be undone. Conversations on legal hold are kept.`)) return;
        try { const r = await api.post(`${API}/retention/purge`, { confirm: true }); alert(`${r.deleted} deleted.`); load(); } catch (e) { setErr(e.message); }
    };
    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={`${card} space-y-2`}>
                <div className="flex flex-wrap gap-2 text-sm">
                    <input placeholder="Search text" value={q.q} onChange={(e) => setQ({ ...q, q: e.target.value })} className="p-1 border rounded flex-1" aria-label="Search transcripts" />
                    <label className="flex items-center gap-1"><input type="checkbox" checked={q.flagged} onChange={(e) => setQ({ ...q, flagged: e.target.checked })} /> flagged</label>
                    <input type="date" value={q.from} onChange={(e) => setQ({ ...q, from: e.target.value })} className="p-1 border rounded" aria-label="From" />
                    <input type="date" value={q.to} onChange={(e) => setQ({ ...q, to: e.target.value })} className="p-1 border rounded" aria-label="To" />
                </div>
                {err && <p className="text-sm text-red-600">{err}</p>}
                <div className="max-h-[32rem] overflow-y-auto">
                    <DataList rows={rows} rowKey="conversation_id" csvName="chat-transcripts" onRowClick={(r) => setSel(r.conversation_id)}
                        defaultSort={{ key: 'started_at', dir: 'desc' }} empty="No transcripts match."
                        rowClass={(r) => (sel === r.conversation_id ? 'bg-indigo-50' : '')}
                        note={`The boxes above ask the server (up to 200 newest); this one searches and sorts the ${rows.length} loaded.`}
                        filters={[{ key: 'last_tier', label: 'Tier' }]}
                        columns={[
                            { key: 'conversation_id', label: 'Chat', value: (r) => `C-${r.conversation_id}`, sortValue: (r) => r.conversation_id },
                            { key: 'started_at', label: 'Started', type: 'date', render: (r) => fmt(r.started_at) },
                            { key: 'message_count', label: 'Msgs', type: 'number' },
                            { key: 'last_tier', label: 'Tier', value: (r) => r.last_tier || '—' },
                            { key: 'client_name', label: 'Visitor' },
                            { key: 'marks', label: 'Marks', value: (r) => [r.flagged ? 'flagged' : '', r.legal_hold ? 'hold' : ''].filter(Boolean).join(' '),
                                render: (r) => <>{r.flagged ? <span className="text-amber-700" title="flagged">⚑</span> : null}{r.legal_hold ? <span className="ml-1 text-xs">(hold)</span> : null}</> },
                        ]} />
                </div>
                {ret && <div className="text-xs text-slate-600 border-t pt-2">Kept for {ret.years} years (records retention). {ret.count} past that.
                    {ret.count > 0 && <button className="ml-2 underline text-red-700" onClick={purge}>Purge them</button>}</div>}
            </div>
            <div className={`${card} lg:col-span-2`}>
                {sel ? (
                    <>
                        <Transcript key={sel} api={api} id={sel} me={me} onChanged={load} />
                        <div className="mt-2 text-xs">
                            <button className="underline" onClick={() => api.post(`${API}/conversations/${sel}/legal-hold`, { hold: true }).then(load)}>Place on legal hold</button>{' · '}
                            <button className="underline" onClick={() => api.post(`${API}/conversations/${sel}/legal-hold`, { hold: false }).then(load)}>Release hold</button>
                        </div>
                    </>
                ) : <p className="text-sm text-slate-500">Pick a transcript.</p>}
            </div>
        </div>
    );
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
function HoursEditor({ value, onChange, label }) {
    const v = value || {};
    const set = (d, i, t) => {
        const span = [...(v[d] || ['09:00', '17:00'])];
        span[i] = t;
        onChange({ ...v, [d]: span });
    };
    const toggle = (d, on) => { const n = { ...v }; if (on) n[d] = ['09:00', '17:00']; else delete n[d]; onChange(n); };
    return (
        <fieldset className="text-sm">
            <legend className="font-medium mb-1">{label}</legend>
            {DAYS.map((d) => (
                <div key={d} className="flex items-center gap-2 mb-1">
                    <label className="w-16 flex items-center gap-1"><input type="checkbox" checked={!!v[d]} onChange={(e) => toggle(d, e.target.checked)} />{d}</label>
                    {v[d] && <>
                        <input type="time" value={v[d][0]} onChange={(e) => set(d, 0, e.target.value)} className="p-1 border rounded" aria-label={`${d} opens`} />
                        <span>–</span>
                        <input type="time" value={v[d][1]} onChange={(e) => set(d, 1, e.target.value)} className="p-1 border rounded" aria-label={`${d} closes`} />
                    </>}
                </div>
            ))}
        </fieldset>
    );
}

function FaqEditor({ api }) {
    const [rows, setRows] = useState([]);
    const [editing, setEditing] = useState(null);
    const [msg, setMsg] = useState('');
    const load = useCallback(() => api.get(`${API}/faq`).then(setRows).catch((e) => setMsg(e.message)), [api]);
    useEffect(() => { load(); }, [load]);
    const save = async () => {
        setMsg('');
        try {
            if (editing.faq_id) await api.put(`${API}/faq/${editing.faq_id}`, editing); else await api.post(`${API}/faq`, editing);
            setEditing(null); load();
        } catch (e) { setMsg(e.message); }
    };
    const seed = async () => { try { const r = await api.post(`${API}/faq/seed`, {}); setMsg(`${r.added} starter answer(s) added for review.`); load(); } catch (e) { setMsg(e.message); } };
    const remove = async (id) => { if (!window.confirm('Delete this answer?')) return; await api.delete(`${API}/faq/${id}`); load(); };
    return (
        <div className={`${card} space-y-3`}>
            <div className="flex items-center gap-2">
                <h3 className="font-semibold">FAQ — the only answers the assistant may give</h3>
                <span className="flex-1" />
                <button className={`${btn} border`} onClick={seed}>Add this state's starter answers</button>
                <button className={`${btn} bg-slate-800 text-white`} onClick={() => setEditing({ question: '', answer: '', keywords: '', source_url: '/contact', source_label: 'Contact', category: 'general', is_published: false })}>New answer</button>
            </div>
            <p className="text-xs text-slate-500">Placeholders filled from settings: {'{{brokerage_name}} {{service_area}} {{office_hours}} {{identity_line}}'}. An answer whose placeholder has no value is never given.</p>
            {msg && <p className="text-sm">{msg}</p>}
            {editing && (
                <div className="border rounded p-3 space-y-2 bg-slate-50 text-sm">
                    <input className="w-full p-2 border rounded" placeholder="Question" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} aria-label="Question" />
                    <textarea className="w-full p-2 border rounded" rows="3" placeholder="Answer" value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} aria-label="Answer" />
                    <input className="w-full p-2 border rounded" placeholder="Extra match words, comma separated" value={editing.keywords || ''} onChange={(e) => setEditing({ ...editing, keywords: e.target.value })} aria-label="Keywords" />
                    <div className="flex gap-2">
                        <input className="flex-1 p-2 border rounded" placeholder="Source page, e.g. /contact" value={editing.source_url || ''} onChange={(e) => setEditing({ ...editing, source_url: e.target.value })} aria-label="Source page" />
                        <input className="flex-1 p-2 border rounded" placeholder="Source label" value={editing.source_label || ''} onChange={(e) => setEditing({ ...editing, source_label: e.target.value })} aria-label="Source label" />
                    </div>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} /> Reviewed — publish this answer</label>
                    <div className="flex gap-2"><button className={`${btn} bg-slate-800 text-white`} onClick={save}>Save</button><button className={`${btn} border`} onClick={() => setEditing(null)}>Cancel</button></div>
                </div>
            )}
            <ul className="divide-y">
                {rows.map((f) => (
                    <li key={f.faq_id} className="py-2 text-sm">
                        <div className="flex gap-2 items-start">
                            <div className="flex-1">
                                <div className="font-medium">{f.question} {f.is_published ? <span className="text-xs text-emerald-700">published</span> : <span className="text-xs text-slate-500">not published</span>}
                                    {f.needs_review ? <span className="ml-1 text-xs text-amber-700">needs review</span> : null}</div>
                                <div className="text-slate-600 whitespace-pre-line">{f.answer}</div>
                                {f.review_note && f.needs_review ? <div className="text-xs text-amber-700">{f.review_note}</div> : null}
                            </div>
                            <button className="text-xs underline" onClick={() => setEditing({ ...f, is_published: !!f.is_published })}>Edit</button>
                            <button className="text-xs underline text-red-700" onClick={() => remove(f.faq_id)}>Delete</button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function Settings({ api, isBroker }) {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState(null);
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState('');
    const load = useCallback(() => {
        api.get(`${API}/settings`).then((d) => { setData(d); setS(d.settings); }).catch((e) => setMsg(e.message));
        api.get(`${API}/status`).then(setStatus).catch(() => {});
    }, [api]);
    useEffect(() => { load(); }, [load]);
    if (!s) return <p className="text-sm text-slate-500">{msg || 'Loading…'}</p>;
    const save = async () => {
        setMsg('');
        const keys = ['widget_enabled', 'basic_enabled', 'conversational_enabled', 'assistant_enabled', 'live_handoff_enabled', 'agent_assistant_enabled',
            'staff_assistant_enabled', 'staff_daily_cost_cap_cents',
            'model', 'max_output_tokens', 'daily_cost_cap_cents', 'monthly_cost_cap_cents', 'daily_message_cap', 'conversation_message_cap',
            'agent_daily_cost_cap_cents', 'greeting', 'timezone', 'offline_message', 'office_hours_json', 'handoff_hours_json',
            'est_rate_bp', 'est_tax_bp', 'est_insurance_bp'];
        const body = Object.fromEntries(keys.map((k) => [k, s[k]]));
        if (s.consent_text !== data.settings.consent_text) body.consent_text = s.consent_text;
        try { await api.put(`${API}/settings`, body); setMsg('Saved.'); load(); } catch (e) { setMsg(e.message); }
    };
    const sw = (k, label, help) => (
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" disabled={!isBroker} checked={!!Number(s[k])} onChange={(e) => setS({ ...s, [k]: e.target.checked ? 1 : 0 })} className="mt-1" />
            <span><span className="font-medium">{label}</span>{help && <span className="block text-xs text-slate-500">{help}</span>}</span></label>
    );
    const num = (k, label) => (
        <label className="text-sm">{label}<input type="number" disabled={!isBroker} value={s[k] ?? ''} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })} className="ml-2 w-28 p-1 border rounded" /></label>
    );
    return (
        <div className="space-y-4">
            {status && (
                <div className={`${card} text-sm grid grid-cols-2 md:grid-cols-4 gap-2`}>
                    <div><div className="text-xs text-slate-500">Answering now with</div><b>{status.tier_now}</b>{status.tier_reason && <div className="text-xs text-amber-700">{status.tier_reason}</div>}</div>
                    <div><div className="text-xs text-slate-500">AI spend today / month</div><b>${(status.spent.day_microusd / 1e6).toFixed(2)} / ${(status.spent.month_microusd / 1e6).toFixed(2)}</b>
                        <div className="text-xs text-slate-500">caps ${(status.caps.daily_cost_cap_cents / 100).toFixed(2)} / ${(status.caps.monthly_cost_cap_cents / 100).toFixed(2)}</div></div>
                    <div><div className="text-xs text-slate-500">Claude API key</div><b>{status.api_key_configured ? 'configured' : 'not set — FAQ only'}</b></div>
                    <div><div className="text-xs text-slate-500">Live chat staff on duty</div><b>{status.staff_available_now ? 'yes' : 'no'}</b></div>
                </div>
            )}
            <div className={`${card} space-y-3`}>
                <h3 className="font-semibold">Switches {!isBroker && <span className="text-xs font-normal text-slate-500">(managing broker only)</span>}</h3>
                {sw('widget_enabled', 'Show the chat on the public website')}
                {sw('basic_enabled', 'Basic — answers from the published FAQ only (no AI)')}
                {sw('conversational_enabled', 'Conversational — AI answers grounded on the FAQ, our facts and our listings, with sources; payment estimates, listing comparisons and market figures', 'Needs the API key.')}
                {sw('assistant_enabled', 'Assistant — also takes contact details with consent, requests showings, call backs, home valuations, commercial site searches, management quotes and listing alerts, hands off to a person', 'Needs the API key. Follow-up emails and texts it writes are sent or held according to the sending settings below.')}
                {sw('live_handoff_enabled', 'Live chat — staff can take conversations over during hand-off hours')}
                {sw('agent_assistant_enabled', 'Agent assistant — the portal assistant for agents (their own book only)', 'Needs the API key.')}
                {sw('staff_assistant_enabled', 'Staff assistant — Ask the Assistant for office staff, the office manager, the managing broker and agents',
                    'Needs the API key. Answers only from records each person may already see (agents: their own book; examiners never). It cannot change anything: a task or a TC step it suggests waits for the person to press Confirm. This switch is for this site only and starts off on every site, Florida included.')}
                <div className="flex flex-wrap gap-4">
                    <label className="text-sm">Model
                        <select disabled={!isBroker} value={s.model} onChange={(e) => setS({ ...s, model: e.target.value })} className="ml-2 p-1 border rounded">
                            {[...new Set([s.model, ...data.models])].map((m) => <option key={m} value={m}>{m}</option>)}
                        </select></label>
                    {num('max_output_tokens', 'Max reply tokens')}
                    {num('daily_cost_cap_cents', 'Daily cap (¢)')}
                    {num('monthly_cost_cap_cents', 'Monthly cap (¢)')}
                    {num('agent_daily_cost_cap_cents', 'Per-agent daily cap (¢)')}
                    {num('staff_daily_cost_cap_cents', 'Per-person staff assistant daily cap (¢)')}
                    {num('daily_message_cap', 'Messages per day')}
                    {num('conversation_message_cap', 'Messages per conversation')}
                </div>
            </div>
            <div className={`${card} space-y-2`}>
                <h3 className="font-semibold">Payment estimate figures</h3>
                <p className="text-xs text-slate-500">The assistant's monthly payment estimate uses these when a visitor does not give their own. Leave a figure at 0 and the
                    assistant asks the visitor instead of assuming one. Every estimate says it is not a loan quote. Update the rate when the market moves.</p>
                <div className="flex flex-wrap gap-4">
                    {[['est_rate_bp', 'Interest rate %'], ['est_tax_bp', 'Property tax % of price / yr'], ['est_insurance_bp', 'Insurance % of price / yr']].map(([k, label]) => (
                        <label key={k} className="text-sm">{label}<input type="number" step="0.01" min="0" disabled={!isBroker}
                            value={s[k] ? (Number(s[k]) / 100).toString() : ''} placeholder="not set"
                            onChange={(e) => setS({ ...s, [k]: Math.round(Number(e.target.value || 0) * 100) })} className="ml-2 w-24 p-1 border rounded" /></label>
                    ))}
                </div>
            </div>
            <div className={`${card} grid grid-cols-1 md:grid-cols-2 gap-4`}>
                <HoursEditor label="Office hours (shown to visitors)" value={s.office_hours_json} onChange={(v) => setS({ ...s, office_hours_json: v })} />
                <HoursEditor label="Live chat hours (blank = office hours)" value={s.handoff_hours_json} onChange={(v) => setS({ ...s, handoff_hours_json: Object.keys(v).length ? v : null })} />
                <label className="text-sm">Time zone (e.g. America/Chicago)<input value={s.timezone || ''} onChange={(e) => setS({ ...s, timezone: e.target.value })} className="block w-full p-1 border rounded" /></label>
                <label className="text-sm">Greeting (after the AI disclosure, which is always shown)<input value={s.greeting || ''} maxLength={500} onChange={(e) => setS({ ...s, greeting: e.target.value })} className="block w-full p-1 border rounded" /></label>
                <label className="text-sm">When no one is on live chat<input value={s.offline_message || ''} maxLength={500} onChange={(e) => setS({ ...s, offline_message: e.target.value })} className="block w-full p-1 border rounded" /></label>
                <label className="text-sm">Consent wording (blank = default; a change starts a new version)
                    <textarea value={s.consent_text || ''} maxLength={1000} rows="3" onChange={(e) => setS({ ...s, consent_text: e.target.value })} className="block w-full p-1 border rounded" /></label>
            </div>
            {isBroker && <div className="flex gap-2 items-center"><button className={`${btn} bg-slate-800 text-white`} onClick={save}>Save settings</button>{msg && <span className="text-sm">{msg}</span>}</div>}
            <OutboundSettings api={api} isBroker={isBroker} />
            <FaqEditor api={api} />
        </div>
    );
}

// Tokens and cost per person per day (AI Use Policy log), both portal-assistant tiers.
function AssistantUsage({ api }) {
    const [d, setD] = useState(null);
    const [days, setDays] = useState(30);
    useEffect(() => { api.get(`${API}/assistant-usage?days=${days}`).then(setD).catch(() => setD({ rows: [], total: { turns: 0, cost_microusd: 0 } })); }, [api, days]);
    if (!d) return <p className="text-sm text-slate-500">Loading…</p>;
    return (
        <div className={card}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
                <h3 className="font-semibold">Portal assistant — usage and cost</h3>
                <label className="text-sm">Last<select value={days} onChange={(e) => setDays(Number(e.target.value))} className="ml-1 p-1 border rounded">
                    {[7, 30, 90, 365].map((n) => <option key={n} value={n}>{n} days</option>)}</select></label>
                <span className="text-sm text-slate-600">{d.total.turns} questions, ${(d.total.cost_microusd / 1e6).toFixed(2)}</span>
            </div>
            <DataList rows={d.rows} rowKey={(r) => `${r.user_id}-${r.usage_date}`} csvName="assistant-usage" defaultSort={{ key: 'usage_date', dir: 'desc' }}
                filters={[{ key: 'username', label: 'Who' }]}
                columns={[
                    { key: 'usage_date', label: 'Day', type: 'date', render: (r) => String(r.usage_date).slice(0, 10) },
                    { key: 'username', label: 'Who' },
                    { key: 'turns', label: 'Questions', type: 'number' },
                    { key: 'input_tokens', label: 'Tokens in', type: 'number' },
                    { key: 'output_tokens', label: 'Tokens out', type: 'number' },
                    { key: 'cost', label: 'Cost', type: 'number', value: (r) => Number(r.cost_microusd || 0) / 1e6, render: (r) => `$${(Number(r.cost_microusd || 0) / 1e6).toFixed(4)}` },
                ]} />
        </div>
    );
}

function AgentAudit({ api }) {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get(`${API}/agent-audit`).then(setRows).catch(() => {}); }, [api]);
    return (
        <div className={card}>
            <h3 className="font-semibold mb-2">Portal assistant — every action (agent and staff tiers)</h3>
            <DataList rows={rows} rowKey="audit_id" csvName="agent-assistant-log" defaultSort={{ key: 'created_at', dir: 'desc' }} note={`Searching the ${rows.length} newest entries loaded (up to 500).`}
                rowClass={(r) => (r.ok ? '' : 'text-amber-800')} filters={[{ key: 'username', label: 'Who' }, { key: 'tier', label: 'Tier' }, { key: 'action_type', label: 'Action' }, { key: 'result', label: 'Result' }]}
                columns={[
                    { key: 'created_at', label: 'When', type: 'date', render: (r) => fmt(r.created_at) },
                    { key: 'username', label: 'Who' },
                    { key: 'tier', label: 'Tier', value: (r) => r.tier || 'agent' },
                    { key: 'action_type', label: 'Action' },
                    { key: 'tool_name', label: 'Tool' },
                    { key: 'detail', label: 'Detail' },
                    { key: 'result', label: 'Result', value: (r) => (r.ok ? 'ok' : 'not ok') },
                ]} />
        </div>
    );
}

const WebsiteChatPage = () => {
    const api = useApiClient();
    const user = getCurrentUser();
    const [params, setParams] = useSearchParams();
    const isBroker = !!user?.isManagingBroker;
    // A receptionist given the website chat inbox by their job access (owner, 26 Oct 2026) answers
    // chats and hand-offs only; the other tabs stay with the managing broker and office manager.
    const inboxOnly = !isBroker && user?.staffRole !== 'Office Manager';
    const tabs = inboxOnly ? [['inbox', 'Inbox']]
        : [['inbox', 'Inbox'], ['outbox', 'Outbox'], ['sends', 'Sends'], ...(isBroker ? [['transcripts', 'Transcripts']] : []), ['settings', 'Settings & FAQ'], ...(isBroker ? [['audit', 'Assistant log']] : [])];
    const tab = tabs.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'inbox';
    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Website Chat</h1>
                <p className="text-sm text-slate-600">The assistant on the public website: who is waiting for a person, messages waiting for approval, every send, transcripts and settings.</p>
            </div>
            <div className="flex gap-1 border-b" role="tablist">
                {tabs.map(([k, label]) => (
                    <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams({ tab: k })}
                        className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === k ? 'border-slate-700 text-indigo-700 font-medium' : 'border-transparent text-slate-600 hover:text-slate-800'}`}>{label}</button>
                ))}
            </div>
            {tab === 'inbox' && <Inbox api={api} me={user} />}
            {tab === 'outbox' && !inboxOnly && <OutboxList api={api} />}
            {tab === 'sends' && !inboxOnly && <div className="space-y-4"><SendsDashboard api={api} /><InboundTexts api={api} /></div>}
            {tab === 'transcripts' && isBroker && <Transcripts api={api} me={user} />}
            {tab === 'settings' && !inboxOnly && <Settings api={api} isBroker={isBroker} />}
            {tab === 'audit' && isBroker && <div className="space-y-4"><AssistantUsage api={api} /><AgentAudit api={api} /></div>}
        </div>
    );
};

export default WebsiteChatPage;
